'use client'

import { FormError } from '@/components/forms/FormError'
import { FormItem } from '@/components/forms/FormItem'
import { Message } from '@/components/Message'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/providers/Auth'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import React, { useCallback, useRef } from 'react'
import { useForm } from 'react-hook-form'

type FormData = {
  email: string
  password: string
}

export const LoginForm: React.FC = () => {
  const searchParams = useSearchParams()
  const allParams = searchParams.toString() ? `?${searchParams.toString()}` : ''
  const redirect = useRef(searchParams.get('redirect'))
  const { login } = useAuth()
  const router = useRouter()
  const [error, setError] = React.useState<null | string>(null)

  const {
    formState: { errors, isLoading },
    handleSubmit,
    register,
  } = useForm<FormData>()

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        await login(data)
        if (redirect?.current) router.push(redirect.current)
        else router.push('/account')
      } catch (_) {
        setError('There was an error with the credentials provided. Please try again.')
      }
    },
    [login, router],
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Message className="mb-6" error={error} />
      <div className="space-y-5">
        <FormItem>
          <Label htmlFor="email" className="text-sm font-medium text-foreground">
            E-mailadres
          </Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="naam@voorbeeld.nl"
            className="h-11 rounded-lg border-border/60 bg-background/50 transition-colors focus:border-primary/50 focus:ring-primary/20"
            autoFocus
            {...register('email', { required: 'E-mailadres is verplicht.' })}
          />
          {errors.email && <FormError message={errors.email.message} />}
        </FormItem>

        <FormItem>
          <Label htmlFor="password" className="text-sm font-medium text-foreground">
            Wachtwoord
          </Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            className="h-11 rounded-lg border-border/60 bg-background/50 transition-colors focus:border-primary/50 focus:ring-primary/20"
            {...register('password', { required: 'Wachtwoord is verplicht.' })}
          />
          {errors.password && <FormError message={errors.password.message} />}
        </FormItem>

        <div className="text-right">
          <Link
            href={`/wachtwoord-vergeten${allParams}`}
            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            Wachtwoord vergeten?
          </Link>
        </div>
      </div>

      <div className="mt-8 space-y-3">
        <Button
          className="w-full h-11 rounded-lg font-medium"
          disabled={isLoading}
          size="lg"
          type="submit"
          variant="default"
        >
          {isLoading ? 'Bezig met inloggen...' : 'Inloggen'}
        </Button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border/60" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card/50 px-3 text-muted-foreground">of</span>
          </div>
        </div>

        <Button asChild variant="outline" size="lg" className="w-full h-11 rounded-lg font-medium">
          <Link href={`/registreren${allParams}`}>Account aanmaken</Link>
        </Button>
      </div>
    </form>
  )
}
